/**
 * Human-readable ERC-20 allowance messaging for MetaMask approve/revoke flows.
 * MetaMask UI itself cannot be customized — we show amount clearly in-app first.
 */

export const formatAllowanceAmountLabel = ({
  amount,
  amountRaw,
  decimals,
  assetSymbol,
} = {}) => {
  const symbol = String(assetSymbol ?? 'TOKEN').trim().toUpperCase() || 'TOKEN';
  const raw = String(amountRaw ?? '').trim();
  const human = Number(amount);

  if (Number.isFinite(human) && human > 0) {
    const formatted = human.toLocaleString('en-US', {
      maximumFractionDigits: 8,
      useGrouping: true,
    });
    return `${formatted} ${symbol}`;
  }

  if ((!raw || raw === '0') && Number.isFinite(human) && human === 0) {
    return `0 ${symbol}`;
  }

  if (!raw || !/^\d+$/.test(raw)) return symbol;

  const safeDecimals =
    Number.isFinite(Number(decimals)) && Number(decimals) >= 0
      ? Math.floor(Number(decimals))
      : 18;

  if (raw === '0') return `0 ${symbol}`;

  if (raw.length <= safeDecimals) {
    const frac = raw.padStart(safeDecimals, '0').replace(/0+$/, '');
    return frac ? `0.${frac} ${symbol}` : `0 ${symbol}`;
  }

  const whole = raw.slice(0, raw.length - safeDecimals).replace(/^0+/, '') || '0';
  const frac = raw.slice(raw.length - safeDecimals).replace(/0+$/, '');
  return frac ? `${whole}.${frac} ${symbol}` : `${whole} ${symbol}`;
};

/**
 * @param {{ mode?: 'approve'|'set'|'revoke', amount?: number|string, amountRaw?: string, decimals?: number, assetSymbol?: string }} ctx
 */
export const buildAllowanceApprovalToastMessage = (ctx = {}) => {
  const mode = ctx.mode || (String(ctx.amountRaw ?? '') === '0' ? 'revoke' : 'approve');
  const amountLabel = formatAllowanceAmountLabel(ctx);
  const symbol = String(ctx.assetSymbol ?? 'TOKEN').trim().toUpperCase() || 'TOKEN';

  if (mode === 'revoke' || String(ctx.amountRaw ?? '') === '0') {
    return `MetaMask will ask you to remove ${symbol} spending permission (set allowance to 0).`;
  }

  if (mode === 'set') {
    return `MetaMask will ask you to update ${symbol} allowance to ${amountLabel}.`;
  }

  return `MetaMask will ask you to approve spending of ${amountLabel}.`;
};
